// ============================================
// Web Push (issue #61 fase 2) — opt-in por dispositivo
// ============================================
// Aberto pelo menu da conta (qualquer papel — operador de campo é o público).
// Honestidade de suporte no próprio diálogo: Android/desktop recebem com o app
// fechado; iPhone/iPad só com o app instalado na tela de início. No mobile o
// Dialog vira bottom sheet pelo tema, de graça.

import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import Typography from '@mui/material/Typography'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import { pushService, type PushUiState } from '../services/pushService'
import { useToast } from './ToastProvider'

type DialogState = PushUiState | 'loading' | 'error'

export default function PushNotificationsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { showToast } = useToast()
  const [state, setState] = useState<DialogState>('loading')
  const [busy, setBusy] = useState(false)
  const [testing, setTesting] = useState(false)

  useEffect(() => {
    if (!open) return
    let vivo = true
    setState('loading')
    pushService
      .getState()
      .then((s) => {
        if (vivo) setState(s)
      })
      .catch(() => {
        if (vivo) setState('error')
      })
    return () => {
      vivo = false
    }
  }, [open])

  const ligado = state === 'on'

  const onToggle = (): void => {
    if (busy) return
    setBusy(true)
    const acao: Promise<DialogState> = ligado
      ? pushService.disable().then(() => 'off' as const)
      : pushService.enable()
    acao
      .then((s) => {
        setState(s)
        if (s === 'on') showToast('Alertas ativados neste dispositivo.', { severity: 'success' })
        else if (s === 'off') showToast('Alertas desativados neste dispositivo.', { severity: 'success' })
        else if (s === 'denied') showToast('O navegador negou a permissão de notificação.', { severity: 'warning' })
      })
      .catch(() => {
        showToast('Não foi possível alterar as notificações. Tente novamente.', { severity: 'error' })
        pushService.getState().then(setState).catch(() => setState('error'))
      })
      .finally(() => setBusy(false))
  }

  const onTest = (): void => {
    setTesting(true)
    pushService
      .sendTest()
      .then((r) => {
        if (r.ok) {
          showToast(
            r.sent > 1 ? `Teste enviado para ${r.sent} dispositivos.` : 'Notificação de teste enviada — confira a bandeja.',
            { severity: 'success' }
          )
        } else if (r.reason === 'no_subscriptions') {
          showToast('Nenhum dispositivo inscrito nesta conta.', { severity: 'warning' })
        } else {
          showToast('O envio de teste falhou em todos os dispositivos.', { severity: 'error' })
        }
      })
      .catch(() => showToast('Não foi possível enviar o teste.', { severity: 'error' }))
      .finally(() => setTesting(false))
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" aria-labelledby="push-dialog-title">
      <DialogTitle id="push-dialog-title">Notificações</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Receba os alertas das suas empresas neste dispositivo — excursão aberta, sensor offline,
            calibração e desvio de relógio — mesmo com o app fechado.
          </Typography>

          {state === 'loading' && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={28} aria-label="Verificando estado das notificações" />
            </Box>
          )}

          {state === 'error' && (
            <Alert severity="error">Não foi possível consultar o estado das notificações. Feche e tente de novo.</Alert>
          )}

          {state === 'server-off' && (
            <Alert severity="info">
              O servidor ainda não tem Web Push configurado (chaves VAPID). Fale com o administrador.
            </Alert>
          )}

          {state === 'unsupported' && (
            <Alert severity="warning">Este navegador não suporta notificações push.</Alert>
          )}

          {state === 'ios-needs-install' && (
            <Alert severity="info">
              No iPhone/iPad as notificações exigem o app instalado: toque em Compartilhar →{' '}
              <strong>Adicionar à Tela de Início</strong> e ative por lá.
            </Alert>
          )}

          {state === 'denied' && (
            <Alert severity="warning">
              As notificações estão bloqueadas para este site. Libere nas permissões do navegador e tente de novo.
            </Alert>
          )}

          {(state === 'on' || state === 'off' || state === 'denied') && (
            <FormControlLabel
              control={
                <Switch
                  checked={ligado}
                  onChange={onToggle}
                  disabled={busy || state === 'denied'}
                  inputProps={{ 'aria-label': 'Receber alertas neste dispositivo' }}
                />
              }
              label={busy ? 'Aplicando…' : 'Receber alertas neste dispositivo'}
            />
          )}

          {state === 'on' && (
            <Button
              onClick={onTest}
              disabled={testing}
              startIcon={testing ? <CircularProgress size={16} /> : <SendRoundedIcon />}
              variant="outlined"
              size="small"
              sx={{ alignSelf: 'flex-start' }}
            >
              Enviar notificação de teste
            </Button>
          )}

          <Typography variant="caption" color="text.secondary">
            Android e computador recebem com o app fechado. iPhone/iPad: somente com o app instalado
            na tela de início (iOS 16.4+).
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  )
}
